<template>
  <div class="serum-description">
    <div class="serum-description__text text-description">
      <h3 class="text-description__title u-typography-helvetica u-text-display u-text-display--s">
        {{ title }}
      </h3>
      <p class="text-description__subtitle u-typography-helvetica u-text u-text--s">
        {{ $t('discover.product.serum.subtitle') }}
      </p>

      <div class="text-description__product-labels product-labels">
        <product-tag
          v-if="isRecommended"
          :is-product-recommended="isRecommended"
          :message="$t('product.drSelectionMessage')"
          class="product-labels__label"
          data-test-id="recommended-label"
        ></product-tag>

        <product-tag
          v-if="isAvailabilityTagVisible"
          :message="statusTagMessage"
          :is-product-in-stock="isProductInStock"
          :is-product-available="isProductAvailable"
          class="product-labels__label"
          data-test-id="status-label"
        />
        <template v-if="!isEyeContourGeneric">
          <product-tag
            :message="$t('product.treatMessage')"
            class="product-labels__label"
            data-test-id="category-label"
          />
          <product-tag
            :message="$t(productUsageLabel)"
            class="product-labels__label usage-label"
            data-test-id="usage-label"
          />
        </template>
      </div>

      <template v-if="!isEyeContourGeneric">
        <text-clamped :max-rows="4">
          <p class="text-description__body u-typography-helvetica u-text u-text--s">
            {{ description }}
          </p>

          <template #show-all-trigger>
            <span
              class="text-description__read-more u-typography-helvetica u-text u-text--link u-text--xs"
              >{{ $t('common.readMore') }}</span
            >
          </template>
        </text-clamped>
      </template>
    </div>

    <div v-if="isEyeContourGeneric" class="generic-formulas">
      <h4 class="serum-description__section-header">
        {{ $t('productSubpage.selectYourFormulaLabel') }}
      </h4>

      <u-toggle-button
        v-for="genericFormulaId of eyeSerumGenericFormulaIds"
        :key="genericFormulaId"
        v-model="selectedGenericFormulaId"
        class="generic-formulas__choice"
        :value="genericFormulaId"
        :label="getGenericFormulaDisplayName(genericFormulaId)"
      />
    </div>

    <div class="serum-description__formulas formulas">
      <h4 class="serum-description__section-header formulas__header">
        {{ $t('discover.product.serum.formulaContains') }}
      </h4>

      <div
        v-for="formula of formulas"
        :key="getFormulaTitle(formula)"
        class="formulas__formula custom-formula ws-col-4"
      >
        <span class="custom-formula__title custom-formula-title">
          <app-icon class="custom-formula-title__icon" :name="getFormulaIconName(formula)" />
          <span class="u-typography-helvetica u-text u-text--s">{{
            getFormulaTitle(formula)
          }}</span>
        </span>

        <div
          v-for="ingredient of getIngredients(formula)"
          :key="getIngredientName(ingredient)"
          class="custom-formula__ingredient ingredient-unit u-typography-helvetica u-text u-text--m"
        >
          <serum-ingredient-color-box
            class="ingredient-unit__color-box"
            :name="getIngredientName(ingredient)"
          />
          <span class="ingredient-unit__title">{{ getIngredientTitle(ingredient) }}</span>
          <span>{{ getIngredientConcentration(ingredient) }}%</span>
        </div>
      </div>
    </div>

    <text-clamped v-if="isEyeContourGeneric" :max-rows="4">
      <p class="serum-description__generic-formula-text u-typography-helvetica u-text u-text--s">
        {{ description }}
      </p>

      <template #show-all-trigger>
        <span
          class="serum-description__generic-formula-read-more u-typography-helvetica u-text u-text--link u-text--xs"
          >{{ $t('common.readMore') }}</span
        >
      </template>
    </text-clamped>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex';
import { UToggleButton } from 'universkin-shared';

import SerumIngredientColorBox from '@/components/common/SerumIngredientColorBox.vue';
import AppIcon, { ICON_NAME } from '@/components/common/AppIcon.vue';
import TextClamped from '@/components/common/TextClamped.vue';
import ProductTag from '@/components/common/ProductTag.vue';

import { formatProductSize } from '@/utils/common';

import {
  getProductOptionsDefaultOption,
  getProductOptionsOptionByFormulaIdOrDefault
} from '@/models/core/ProductOptions';
import {
  getProductPackItems,
  getProductPackSize,
  getProductPackUsage,
  isProductPackGenericEyeSerum,
  getProductPackName,
  isProductPackAvailable,
  isProductPackInStock
} from '@/models/core/ProductPack';
import { getPackItemIngredients, getPackItemUsage } from '@/models/core/PackItem';
import { getSizeUnits, getSizeValue } from '@/models/core/values';
import { getIngredientName, getIngredientTotalConcentration } from '@/models/core/Ingredient';

import {
  PRODUCT_NAMES,
  SERUM_USAGES,
  FORMULA_USAGE,
  EYE_SERUM_GENERIC_FORMULA_LABELS,
  EYE_SERUM_GENERIC_FORMULA_DESCRIPTIONS
} from '@/constants/treatment';
import { PRODUCTS_CONFIGURATION } from '@/constants/your-routine';
import { types } from '@/store/types';

const DESCRIPTION = {
  [PRODUCT_NAMES.FACE_SERUM]: 'discover.product.serum.face.customSerumDescription',
  [PRODUCT_NAMES.EYE_CONTOUR_SERUM]: 'discover.product.serum.eye.customSerumDescription'
};

const PRODUCT_USAGE_LABEL = {
  [SERUM_USAGES.MORNING_ONLY]: 'discover.usage.am',
  [SERUM_USAGES.EVENING_ONLY]: 'discover.usage.pm',
  [SERUM_USAGES.ONE_MORNING_ONE_EVENING]: 'discover.usage.amAndPm',
  [SERUM_USAGES.MORNING_AND_EVENING]: 'discover.usage.amAndPm'
};

const FORMULA_ICON_NAME = {
  [FORMULA_USAGE.MORNING_ONLY]: ICON_NAME.SUN,
  [FORMULA_USAGE.EVENING_ONLY]: ICON_NAME.MOON,
  [FORMULA_USAGE.MORNING_AND_EVENING]: ICON_NAME.SUN_AND_MOON
};

const FORMULA_TITLE = {
  [SERUM_USAGES.MORNING_ONLY]: 'welcomeSection.usage.morningLabel',
  [SERUM_USAGES.EVENING_ONLY]: 'welcomeSection.usage.eveningLabel',
  [SERUM_USAGES.MORNING_AND_EVENING]: 'welcomeSection.usage.morningAndEveningLabel'
};

const DUPLICATED_FORMULA_TITLE = {
  [SERUM_USAGES.MORNING_ONLY]: 'welcomeSection.usage.duplicatedMorningLabel',
  [SERUM_USAGES.EVENING_ONLY]: 'welcomeSection.usage.duplicatedEveningLabel',
  [SERUM_USAGES.MORNING_AND_EVENING]: 'welcomeSection.usage.duplicatedMorningAndEveningLabel'
};

export default {
  name: 'SerumProductDescription',
  components: { SerumIngredientColorBox, AppIcon, UToggleButton, TextClamped, ProductTag },
  props: {
    productName: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapState({
      locale: types.state.LOCALE,
      selectedEyeContourGenericFormulaId: types.state.SELECTED_EYE_SERUM_GENERIC_FORMULA_ID
    }),
    ...mapGetters({
      allProductOptions: types.getters.ALL_PRODUCT_OPTIONS_SETS,
      eyeSerumGenericFormulaIds: types.getters.EYE_SERUM_GENERIC_FORMULA_IDS,
      getIsProductRecommended: types.getters.GET_IS_PRODUCT_RECOMMENDED
    }),
    product() {
      const productOptions = this.allProductOptions[this.productName];
      const defaultOption = getProductOptionsDefaultOption(productOptions);

      if (isProductPackGenericEyeSerum(defaultOption)) {
        return getProductOptionsOptionByFormulaIdOrDefault(
          this.selectedGenericFormulaId,
          productOptions
        );
      }

      return defaultOption;
    },
    selectedGenericFormulaId: {
      get() {
        return this.selectedEyeContourGenericFormulaId;
      },
      set(formulaId) {
        this.setSelectedEyeSerumGenericFormulaId(formulaId);
      }
    },
    isEyeContourGeneric() {
      return isProductPackGenericEyeSerum(this.product);
    },
    isRecommended() {
      return this.getIsProductRecommended(this.productName);
    },
    description() {
      if (this.isEyeContourGeneric) {
        return this.$t(EYE_SERUM_GENERIC_FORMULA_DESCRIPTIONS[this.selectedGenericFormulaId]);
      }

      return this.$t(DESCRIPTION[this.productName]);
    },
    title() {
      const { title, titleRecommended } = PRODUCTS_CONFIGURATION[this.productName];

      return this.isRecommended && titleRecommended ? this.$t(titleRecommended) : this.$t(title);
    },
    usage() {
      return getProductPackUsage(this.product);
    },
    formattedSize() {
      const size = getProductPackSize(this.product);
      const value = getSizeValue(size);
      const units = getSizeUnits(size);

      return formatProductSize({ value, units }, this.locale);
    },
    formulas() {
      const usage = getProductPackUsage(this.product);
      const packItems = getProductPackItems(this.product);

      return usage === SERUM_USAGES.ONE_MORNING_ONE_EVENING ? packItems : [packItems[0]];
    },
    productUsageLabel() {
      return PRODUCT_USAGE_LABEL[this.usage];
    },
    isProductInStock() {
      return isProductPackInStock(this.product);
    },
    isProductAvailable() {
      return isProductPackAvailable(this.product);
    },
    isAvailabilityTagVisible() {
      return !this.isProductInStock || !this.isProductAvailable;
    },
    statusTagMessage() {
      return !this.isProductAvailable
        ? this.$t('product.comingSoonMessage')
        : this.$t('product.outOfStockMessage');
    }
  },
  methods: {
    ...mapMutations({
      setSelectedEyeSerumGenericFormulaId: types.mutations.SET_SELECTED_EYE_SERUM_GENERIC_FORMULA_ID
    }),
    getIngredientTitle(ingredient) {
      const name = getIngredientName(ingredient);

      return this.$t(`section.yourIngredients.ingredient.${name}.label`);
    },
    getIngredientConcentration(ingredient) {
      const concentration = getIngredientTotalConcentration(ingredient);

      return concentration.toFixed(1);
    },
    getFormulaTitle(formula) {
      const isEyeContourSerum =
        getProductPackName(this.product) === PRODUCT_NAMES.EYE_CONTOUR_SERUM;
      const usage = getPackItemUsage(formula);

      const multiplicity = this.formulas.length > 1;

      return multiplicity || isEyeContourSerum
        ? this.$t(FORMULA_TITLE[usage])
        : this.$t(DUPLICATED_FORMULA_TITLE[usage]);
    },
    getFormulaIconName(formula) {
      const usage = getPackItemUsage(formula);

      return FORMULA_ICON_NAME[usage];
    },
    getIngredientName(ingredient) {
      return getIngredientName(ingredient);
    },
    getIngredients(formula) {
      return getPackItemIngredients(formula);
    },
    getGenericFormulaDisplayName(formulaId) {
      return this.$t(EYE_SERUM_GENERIC_FORMULA_LABELS[formulaId]);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/colors.scss';

.serum-description {
  &__section-header {
    font-family: var(--u-typography-Helvetica);
    font-weight: bold;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $tuatara-color;
    margin-top: 24px;
    margin-bottom: 16px;
  }

  &__generic-formula-text {
    color: var(--u-color-grey-900);
    margin-top: 24px;
  }

  &__generic-formula-read-more {
    display: inline-block;
    margin-top: 20px;
    cursor: pointer;
  }

  &__formulas {
    border-bottom: 1px solid var(--u-color-grey-300);
  }
}

.text-description {
  &__title {
    color: var(--u-color-graphite);
    margin-bottom: 8px;
  }

  &__subtitle {
    margin-bottom: 8px;
    color: var(--u-color-grey-900);
  }

  &__body {
    color: var(--u-color-grey-900);
  }

  &__read-more {
    display: inline-block;
    margin-top: 20px;
    cursor: pointer;
  }
}

.product-labels {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10px;

  &__label {
    margin-right: 12px;
    margin-bottom: 14px;
  }
}

.generic-formulas {
  display: flex;
  flex-direction: column;
  width: fit-content;

  &__choice:not(:last-child) {
    margin-bottom: 8px;
  }
}

.formulas {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;

  &__header {
    width: 100%;
  }

  &__formula {
    margin-bottom: 24px;
  }

  &__formula:not(:last-child) {
    margin-inline-end: 24px;
  }
}

.custom-formula {
  &__title {
    color: var(--u-color-grey-600);
    margin-bottom: 16px;
  }

  &__ingredient:not(:last-child) {
    margin-bottom: 16px;
  }

  &:nth-of-type(1) {
    margin-bottom: 20px;
  }
}

.custom-formula-title {
  display: flex;
  align-items: center;

  &__icon {
    margin-inline-end: 8px;
  }
}

.ingredient-unit {
  display: flex;
  align-items: center;

  &__color-box,
  &__title {
    margin-inline-end: 8px;
  }
}

@media (min-width: 1200px) {
  .formulas {
    &__formula {
      width: fit-content;
    }
  }
}
</style>
